import type { AudioFilterEntryConfig } from "@/types/metrics/metadata"

type AudioFilterEntryType = AudioFilterEntryConfig["type"]

const ALL_FILTER_TYPES: readonly AudioFilterEntryType[] = [
  "speaker",
  "silence",
  "overlap",
  "music",
  "time_range",
  "turn_range",
] as const

// TODO: replace with BE-derived schema once the transport-layer type generation lands.
export const AUDIO_FILTER_TYPE_SUPPORT: Record<
  string,
  readonly AudioFilterEntryType[]
> = {
  manager_clipping_artifact: ["speaker", "time_range", "turn_range"],
  manager_codec_artifact: ["speaker", "time_range", "turn_range"],
  manager_dropout_artifact: ["speaker", "time_range", "turn_range"],
  manager_speech_artifact_anomaly: ["speaker", "time_range", "turn_range"],
  pause_analysis_metric: ["speaker", "time_range", "turn_range"],
  manager_audio_sentiment: ["speaker"],
  manager_phonemes_per_second: ["speaker"],
  manager_timbre_drift: ["speaker", "time_range", "turn_range"],
  manager_transcription_error: ["speaker", "time_range", "turn_range"],
  manager_agent_needs_reprompting: ["speaker"],
  manager_custom_agent_needs_reprompting: [
    "speaker",
    "time_range",
    "turn_range",
  ],
  manager_non_expressive_pauses: ["speaker"],
  manager_abrupt_pitch_changes: ["speaker", "time_range", "turn_range"],
  manager_pitch_variability: ["speaker", "time_range", "turn_range"],
  manager_lufs: ["speaker"],
  manager_terminal_contour: ["speaker", "time_range", "turn_range"],
  manager_vocal_fry: ["speaker", "time_range", "turn_range"],
  manager_volume_pitch_misalignment: ["speaker", "time_range", "turn_range"],
  manager_volume: ["speaker"],
  manager_spectrogram_pitch_analysis: ["speaker", "time_range", "turn_range"],
  manager_voice_quality: ["speaker", "time_range", "turn_range"],
  manager_phoneme_stretch: ["speaker", "time_range", "turn_range"],
  manager_syllable_rate: ["speaker", "time_range", "turn_range"],
  manager_loop_detection: ["speaker", "time_range", "turn_range"],
  manager_custom_audio_frequency: ["speaker", "time_range", "turn_range"],
  manager_speaking_time_percentage: ["speaker", "silence", "music"],
  manager_time_to_first_audio: ["time_range", "turn_range"],
  manager_music_detection: ["time_range", "turn_range"],
  interruption_rate_manager: ["speaker", "time_range", "turn_range"],
  audio_duration_metric_manager: ALL_FILTER_TYPES,
  manager_audio_frequency: ALL_FILTER_TYPES,
  manager_signal_to_noise_ratio: ALL_FILTER_TYPES,
  manager_audio_llm_binary: ALL_FILTER_TYPES,
  manager_audio_llm_categorical: ALL_FILTER_TYPES,
  manager_audio_llm_judge: ALL_FILTER_TYPES,
  manager_audio_llm_numerical: ALL_FILTER_TYPES,
}

export function getSupportedFilterTypes(
  metricManager: string,
): readonly AudioFilterEntryType[] {
  return AUDIO_FILTER_TYPE_SUPPORT[metricManager] ?? ALL_FILTER_TYPES
}

// Managers that opt into one speaker include max, no speaker exclude, and
// single-select roles. Other filter types still stack normally. Managers whose
// directory entry sets `requires_single_speaker` lock the same way; the
// artifact detectors stay listed so the lock holds before the directory loads.
const SINGLE_SPEAKER_FILTER_MANAGERS: ReadonlySet<string> = new Set([
  "manager_abrupt_pitch_changes",
  "manager_custom_agent_needs_reprompting",
  "manager_custom_audio_frequency",
  "manager_loop_detection",
  "manager_lufs",
  "manager_phoneme_stretch",
  "manager_pitch_variability",
  "manager_spectrogram_pitch_analysis",
  "manager_syllable_rate",
  "manager_terminal_contour",
  "manager_timbre_drift",
  "manager_vocal_fry",
  "manager_voice_quality",
  "manager_volume_pitch_misalignment",
  "manager_speech_artifact_anomaly",
  "manager_clipping_artifact",
  "manager_codec_artifact",
  "manager_dropout_artifact",
])

export function isSingleSpeakerFilterManager(metricManager: string): boolean {
  return SINGLE_SPEAKER_FILTER_MANAGERS.has(metricManager)
}

// Managers where the speaker role is locked to "agent" (e.g. metrics that only
// make sense for agent-side behavior). Implies single-speaker mode and removes
// persona / music from the speaker picker.
const AGENT_ONLY_FILTER_MANAGERS: ReadonlySet<string> = new Set([
  "manager_custom_agent_needs_reprompting",
])

export function isAgentOnlyFilterManager(metricManager: string): boolean {
  return AGENT_ONLY_FILTER_MANAGERS.has(metricManager)
}

// Drops "speaker" from the exclude side for single-speaker managers.
export function getSupportedFilterTypesForSide(
  metricManager: string,
  side: "include" | "exclude",
): readonly AudioFilterEntryType[] {
  const baseTypes = getSupportedFilterTypes(metricManager)
  if (side === "exclude" && isSingleSpeakerFilterManager(metricManager)) {
    return baseTypes.filter((type) => type !== "speaker")
  }
  return baseTypes
}
